import { logger } from '../../../../../utils/common/logger'
import { DOMUtils } from '../../../../../utils/dom'
import '../styles/password-requirements.scss' // tslint:disable-line: no-import-side-effect

const PASSWORD_MIN_CHARACTER = 10
const NUMBER_REGEX = /\d/
const UPPER_LOWER_REGEX = /([A-Z].*[a-z]|[a-z].*[A-Z])/
const SPECIAL_CHAR_REGEX = /[$@#%^&*!~`()\-_+={}\\\[\]|:;"'<>,.?/]/
const PASTE_EVENT_TIMEOUT = 100 // time needed to paste text on to the input field

export class PasswordRequirementsList {
    public static passwordInputValue: string
    private static DOM = {
        class: {
            passwordContainer: 'content-loginradius-password',
            passwordRequirementsWrapper: 'password-requirements-wrapper',
            showPasswordRequirementsWrapper: 'password-requirements-wrapper--show',
            validRequirement: 'password-requirement--valid'
        },
        id: {
            passwordRequirementsWrapper: 'password-requirements-wrapper'
        }
    }

    private static checklistItems: NodeList

    /**
     * Add the requirements list html and event listeners
     */
    public static async initDOM() {
        const passwordWrapper = document.querySelector(`.${this.DOM.class.passwordContainer}`)

        if (passwordWrapper) {
            const passwordInput = passwordWrapper.querySelector('[type=password]')

            try {
                const mod = await import('../templates/password-requirements-list/password-requirements-list.htm')
                const checklistDOM = mod.default
                const wrapper = document.createElement('div')

                DOMUtils.addClass(wrapper, this.DOM.class.passwordRequirementsWrapper)
                wrapper.id = this.DOM.id.passwordRequirementsWrapper
                wrapper.innerHTML = checklistDOM
                passwordWrapper.after(wrapper)

                this.checklistItems = wrapper.querySelectorAll('li')

                passwordInput.setAttribute('aria-describedby', this.DOM.id.passwordRequirementsWrapper)

                passwordInput.addEventListener('keyup', (event) => {
                    this.validateAndUpdateChecklistUI(event)
                })

                passwordInput.addEventListener('paste', (event) => {
                    setTimeout(() => {
                        this.validateAndUpdateChecklistUI(event)
                    }, PASTE_EVENT_TIMEOUT)
                })

                passwordInput.addEventListener('focus', () => {
                    this.showPasswordRequirementsList()
                })

                logger('info', 'Password requirements list initialized')
            } catch (e) {
                logger('error', 'Password requirements list not initialized', e)
            }
        }
    }

    /**
     * Checks if password meets the minimum length requirement
     *
     * @param password string
     * @returns boolean
     */
    public static validateMinCharacters(password: string): boolean {
        return password.length >= PASSWORD_MIN_CHARACTER
    }

    /**
     * Checks if password has at least 1 number
     *
     * @param password string
     * @returns boolean
     */
    public static validateNumber(password: string): boolean {
        return NUMBER_REGEX.test(password)
    }

    /**
     * Checks if password has at least 1 uppercase letter and 1 lowercase letter
     *
     * @param password string
     * @returns boolean
     */
    public static validateUppercaseAndLowercase(password: string): boolean {
        return UPPER_LOWER_REGEX.test(password)
    }

    /**
     * Checks if password has at least 1 special character
     *
     * @param password string
     * @returns boolean
     */
    public static validateSpecialCharacter(password: string): boolean {
        return SPECIAL_CHAR_REGEX.test(password)
    }

    /**
     * Check if password passes all validation requirements. Returns a results array
     *
     * @returns boolean[]
     */
    public static validateRequirements(): boolean[] {
        const validationResult = []
        validationResult.push(this.validateMinCharacters(this.passwordInputValue))
        validationResult.push(this.validateNumber(this.passwordInputValue))
        validationResult.push(this.validateUppercaseAndLowercase(this.passwordInputValue))
        validationResult.push(this.validateSpecialCharacter(this.passwordInputValue))
        return validationResult
    }

    /**
     * Update checklist UI
     *
     * @param checklistItems NodeList
     * @param validationResult boolean[]
     */
    public static updateChecklistUI(validationResult: boolean[]) {
        this.checklistItems.forEach((checklistItem: HTMLLinkElement, index) => {
            const valid = validationResult[index]

            if (valid) {
                DOMUtils.addClass(checklistItem, this.DOM.class.validRequirement)
            } else {
                DOMUtils.removeClass(checklistItem, this.DOM.class.validRequirement)
            }
        })
    }

    /**
     * Show password requirements list
     */
    public static showPasswordRequirementsList() {
        if (!document.querySelector(`.${this.DOM.class.showPasswordRequirementsWrapper}`)) {
            const element = document.querySelector(`.${this.DOM.class.passwordRequirementsWrapper}`)
            DOMUtils.addClass(element, this.DOM.class.showPasswordRequirementsWrapper)
        }
    }

    /**
     * Validate and update checklist UI
     *
     * @param event Event
     *
     */
    private static validateAndUpdateChecklistUI(event: Event) {
        const element = event.target as HTMLInputElement
        this.passwordInputValue = element.value

        // validate password passwordInput
        const validationResults = this.validateRequirements()
        this.updateChecklistUI(validationResults)
    }
}
